import React, { useEffect, useState } from 'react'

import GradientButton from '../../components/GradientButton'
import { useT } from '../../i18n'
import { readTransferProtocol, sortBands, type TransferProtocolBand } from '../../utils/transferProtocol'

// Findings #210: Protocolo de traslado — by weight band for the facility's
// species: fasting days before a move, days of reduced ration after it, and
// the Δ °C taken off the water temperature to calculate that ration.
// Kept in `Farm.metadata.transferProtocol` (no migration).

type Draft = { maxWeightG: string; fastDays: string; reducedDays: string; deltaC: string }

const toDraft = (band: TransferProtocolBand): Draft => ({
  maxWeightG: band.maxWeightG === null ? '' : String(band.maxWeightG),
  fastDays: String(band.fastDays),
  reducedDays: String(band.reducedDays),
  deltaC: String(band.deltaC)
})

const parse = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.')
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

export const parseBands = (drafts: Draft[]): TransferProtocolBand[] | null => {
  const bands: TransferProtocolBand[] = []
  let open = 0
  for (const draft of drafts) {
    if (!draft.maxWeightG.trim() && !draft.fastDays.trim() && !draft.reducedDays.trim() && !draft.deltaC.trim()) continue
    const maxWeightG = draft.maxWeightG.trim() ? parse(draft.maxWeightG) : null
    const fastDays = parse(draft.fastDays) ?? 0
    const reducedDays = parse(draft.reducedDays) ?? 0
    const deltaC = parse(draft.deltaC) ?? 0
    if (draft.maxWeightG.trim() && (maxWeightG === null || maxWeightG <= 0)) return null
    if (!Number.isInteger(fastDays) || fastDays < 0 || fastDays > 30) return null
    if (!Number.isInteger(reducedDays) || reducedDays < 0 || reducedDays > 30) return null
    if (deltaC < 0 || deltaC > 20) return null
    if (maxWeightG === null) open += 1
    bands.push({ maxWeightG, fastDays, reducedDays, deltaC })
  }
  if (open > 1) return null
  const weights = bands.map((band) => band.maxWeightG).filter((weight): weight is number => weight !== null)
  if (new Set(weights).size !== weights.length) return null
  return sortBands(bands)
}

export type FacilityTransferProtocolProps = {
  farm: { id: string; metadata?: Record<string, unknown> }
  speciesLabel: string | null
  disabled: boolean
  onSave: (bands: TransferProtocolBand[]) => Promise<void>
}

export default function FacilityTransferProtocol({ farm, speciesLabel, disabled, onSave }: FacilityTransferProtocolProps) {
  const t = useT()
  const saved = readTransferProtocol(farm)
  const savedKey = JSON.stringify(saved)
  const [drafts, setDrafts] = useState<Draft[]>(() => saved.map(toDraft))
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDrafts(readTransferProtocol(farm).map(toDraft))
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm.id, savedKey])

  const update = (index: number, field: keyof Draft, value: string) => {
    setDrafts((prev) => prev.map((draft, position) => (position === index ? { ...draft, [field]: value } : draft)))
  }

  const save = async () => {
    const bands = parseBands(drafts)
    if (!bands) {
      setError(t('editFarm.transferProtocol.invalid'))
      return
    }
    setError(null)
    setSaving(true)
    try {
      await onSave(bands)
    } finally {
      setSaving(false)
    }
  }

  const cellInput: React.CSSProperties = { padding: '6px 8px', border: '1px solid #cbd5f5', borderRadius: 8, width: 80 }
  const head: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 700, padding: '4px 6px', color: '#475569' }

  return (
    <div data-testid="edit-farm-transfer-protocol" style={{ marginTop: 12, display: 'grid', gap: 8 }}>
      <strong>{t('editFarm.transferProtocol.title')}{speciesLabel ? ` (${speciesLabel})` : ''}</strong>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={head}>{t('editFarm.transferProtocol.col.maxWeight')}</th>
              <th style={head}>{t('editFarm.transferProtocol.col.fastDays')}</th>
              <th style={head}>{t('editFarm.transferProtocol.col.reducedDays')}</th>
              <th style={head}>{t('editFarm.transferProtocol.col.deltaC')}</th>
              <th style={head} />
            </tr>
          </thead>
          <tbody>
            {drafts.map((draft, index) => (
              <tr key={index} data-testid={`edit-farm-transfer-protocol-row-${index}`}>
                {(['maxWeightG', 'fastDays', 'reducedDays', 'deltaC'] as const).map((field) => (
                  <td key={field} style={{ padding: '2px 6px' }}>
                    <input
                      data-testid={`edit-farm-transfer-protocol-${field}-${index}`}
                      inputMode="decimal"
                      value={draft[field]}
                      placeholder={field === 'maxWeightG' ? t('editFarm.transferProtocol.openBand') : '0'}
                      disabled={disabled}
                      onChange={(event) => update(index, field, event.target.value)}
                      style={cellInput}
                    />
                  </td>
                ))}
                <td style={{ padding: '2px 6px' }}>
                  <GradientButton
                    size="xs"
                    variant="secondary"
                    disabled={disabled}
                    onClick={() => setDrafts((prev) => prev.filter((_, position) => position !== index))}
                  >
                    {t('editFarm.transferProtocol.removeRow')}
                  </GradientButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <GradientButton
          size="xs"
          variant="info"
          disabled={disabled}
          onClick={() => setDrafts((prev) => [...prev, { maxWeightG: '', fastDays: '', reducedDays: '', deltaC: '' }])}
          data-testid="edit-farm-transfer-protocol-add"
        >
          {t('editFarm.transferProtocol.addRow')}
        </GradientButton>
        <GradientButton
          size="xs"
          variant="success"
          disabled={disabled || saving}
          onClick={() => void save()}
          data-testid="edit-farm-transfer-protocol-save"
        >
          {t('common.save')}
        </GradientButton>
      </div>
      <span style={{ fontSize: 12, color: '#6b7280' }}>{t('editFarm.transferProtocol.help')}</span>
      {error ? <div role="alert" style={{ color: '#b91c1c', fontWeight: 700, fontSize: 13 }}>{error}</div> : null}
    </div>
  )
}
